/**
 * #2118 — "Your contacts": each linked client's Power Users and Stewards, in
 * the shape the employer's chat window renders.
 *
 * Ed, logged in as employer5 on stage-, found the employer's window showing
 * only Search and Support: *"this is not inuitive at all."* An employer knows
 * which locals they work with, not which people at them — so a search box is
 * the wrong affordance for the one role that cannot guess a name.
 *
 * Pure, and separate from `chatService.js` for the reason `chatPeople.js` is:
 * the service module pulls in axios, `./api` (Vue + Vuex + the store) and the
 * Firebase SDK and does not load under node, and the mistakes here are the
 * silent kind — a client heading with no name, a group rendered empty, or the
 * section appearing for a role the server answers `[]` for.
 *
 * Against cs3-chat #36 (MR !24, `b081afaa` on the stage chat box):
 *
 *   GET /api/me/contacts -> { data: [ { client_id, client_name,
 *                                      client_subdomain,
 *                                      people: [ { uid, name, role } ] } ] }
 *
 * ⚠ THE SERVER DECIDES WHO IS IN IT. cs3-chat answers `[]` for every role but
 * employer, omits clients with no PU or Steward, and applies the per-client
 * access rule (#2115's `employer_clients`). Nothing is filtered again here: a
 * second reading of that rule in the browser could only ever disagree with the
 * one that decides. What this module does is SHAPE and GROUP.
 */

import { normalisePeople } from './chatPeople.js';

/**
 * The one role that gets the section.
 *
 * ⚠ ROLE, NOT EMPTINESS, DECIDES WHETHER THE SECTION EXISTS — and the two
 * cases look identical in the response, which is the trap. Both a Member and
 * an employer with no switched-on client get `{"data":[]}`:
 *
 *   employer, empty  -> the section, with "No contacts yet…" (the issue's copy)
 *   anyone else      -> NO section at all, and no heading
 *
 * Deciding on the array alone would give every Member in the fleet an empty
 * "Your contacts" heading, which is worse than the problem being fixed.
 */
export const CONTACTS_ROLE = 'employer';

export function showsContacts(chatRole) {
    return chatRole === CONTACTS_ROLE;
}

/**
 * Caps, mirroring the server's (cs3-chat #36: 50 per client, 500 overall).
 *
 * ⚠ HERE AS A CEILING ON THE DOM, NOT AS A SECOND OPINION. The server already
 * truncates; these only stop a malformed or stale answer from rendering
 * thousands of buttons into the panel. Same reason `chatPeople.MAX_RESULTS`
 * exists beside the server's own limit.
 */
export const MAX_PER_CLIENT = 50;
export const MAX_TOTAL = 500;

/**
 * One client's heading, as the panel draws it.
 *
 * ⚠ `client_name` CAN BE NULL and the fallback is the subdomain, not a blank.
 * cs3-chat sends what CS3 gave it, and `clients.name` is nullable — a heading
 * reading "cs3demo" is useful, an empty one looks like a rendering fault. This
 * is the same choice `ChatHandoffController::name()` makes server-side for the
 * settings heading, for the same reason.
 */
export function groupTitle(raw) {
    const r = raw || {};
    const name = String(r.client_name == null ? '' : r.client_name).trim();
    if (name !== '') return name;

    return String(r.client_subdomain == null ? '' : r.client_subdomain).trim();
}

/**
 * The API's `data` as the panel's groups.
 *
 * - people go through `normalisePeople`, so a contact and a search result are
 *   ONE shape (avatar colour, initials, the presence dot the panel fills in).
 *   Two shapes is how `pickPerson` works on one list and not the other;
 * - a group with no renderable people is DROPPED, not shown empty. The server
 *   already omits clients with no PU or Steward, so an empty one here means a
 *   row whose people were all unusable — a heading over nothing is a defect
 *   either way;
 * - a group with no title at all is dropped for the same reason: there is
 *   nothing to head it with.
 *
 * @param {Array} rows the API's `data`
 */
export function normaliseContactGroups(rows) {
    if (!Array.isArray(rows)) return [];

    const groups = [];
    let total = 0;

    for (const raw of rows) {
        if (total >= MAX_TOTAL) break;

        const title = groupTitle(raw);
        if (title === '') continue;

        const people = normalisePeople((raw || {}).people)
            .slice(0, MAX_PER_CLIENT)
            .slice(0, MAX_TOTAL - total);
        if (people.length === 0) continue;

        total += people.length;
        groups.push({
            // `key` for Vue's :key, and the client id is the one stable value
            // here — a title can repeat across silos and can be a subdomain.
            key: 'cc:' + String((raw || {}).client_id == null ? title : raw.client_id),
            clientId: (raw || {}).client_id == null ? null : String(raw.client_id),
            title,
            people,
        });
    }

    return groups;
}

/** How many people are on screen, for the empty test and for a count if one is wanted. */
export function contactsCount(groups) {
    if (!Array.isArray(groups)) return 0;

    return groups.reduce((n, g) => n + ((g && Array.isArray(g.people)) ? g.people.length : 0), 0);
}

/**
 * `GET /api/me/contacts` failures, as codes the panel explains.
 *
 * Mirrors `peopleFailureCode`: the panel has one place that turns a code into a
 * sentence, and a bare status reaching the template is how "chat is
 * unavailable" ends up shown for a permission refusal (#2096's review found
 * exactly that).
 */
export function contactsFailureCode(status) {
    return {
        401: 'chat_session_expired',
        403: 'chat_not_permitted',
    }[status] || 'chat_unreachable';
}
